export default function BuyerTenantRelationship() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <i className="ri-team-line"></i>
            <span>Relationship Building</span>
          </div>
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Facilitating Buyer-Tenant Relationships
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Smooth transitions between ownership require careful management of buyer-tenant introductions and relationship building.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div className="relative">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/8e1b41e70791732dcdb3e58303a208a9.jpg"
                alt="Buyer and tenant relationship building during property ownership transition"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-6">Why Buyer-Tenant Introductions Matter</h3>
            
            <p className="text-slate-700 mb-6 leading-relaxed">
              The transition from your ownership to the buyer's ownership is a critical period for tenant retention. Proper introductions and relationship building reduce tenant anxiety, prevent move-outs, and set the stage for successful long-term tenancies under new management.
            </p>

            <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg mb-8">
              <div className="flex items-start gap-3">
                <i className="ri-user-heart-line text-green-600 text-2xl mt-1"></i>
                <div>
                  <h4 className="font-semibold text-green-900 mb-2">Retention Impact</h4>
                  <p className="text-green-800 text-sm leading-relaxed">
                    Properties where buyers meet tenants before closing experience 15-25% lower turnover in the first year compared to properties with no pre-closing tenant contact.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4">When to Introduce Buyer to Tenants</h3>

            <div className="space-y-4 mb-8">
              <div className="bg-slate-50 p-5 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-close-circle-line text-red-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Too Early: During Initial Showings</h4>
                    <p className="text-slate-600 text-sm">
                      Don't introduce buyers to tenants during property tours before an offer is accepted. This creates unnecessary tenant anxiety and may not result in a sale.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 p-5 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-line text-green-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Ideal: After Inspection Period</h4>
                    <p className="text-slate-600 text-sm mb-3">
                      Once the buyer has completed due diligence and is committed to closing, facilitate introductions. This is typically 2-4 weeks before closing.
                    </p>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-2 text-slate-600">
                        <i className="ri-calendar-line text-green-600"></i>
                        <span>2-4 weeks pre-closing</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <i className="ri-shield-check-line text-green-600"></i>
                        <span>Deal is solid</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 p-5 rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-error-warning-line text-amber-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Acceptable: At Closing</h4>
                    <p className="text-slate-600 text-sm">
                      If pre-closing introductions aren't possible, provide buyer with tenant contact information at closing and facilitate introductions within 24-48 hours.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4">Introduction Methods</h3>

            <div className="space-y-3">
              <div className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm border border-slate-200">
                <i className="ri-mail-line text-teal-600 text-xl mt-0.5"></i>
                <div>
                  <p className="font-semibold text-slate-900 text-sm mb-1">Written Introduction Letter</p>
                  <p className="text-slate-600 text-xs">Send formal letter from you introducing the new owner, their contact information, and reassuring tenants about lease continuity</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm border border-slate-200">
                <i className="ri-group-line text-teal-600 text-xl mt-0.5"></i>
                <div>
                  <p className="font-semibold text-slate-900 text-sm mb-1">Property Meet-and-Greet</p>
                  <p className="text-slate-600 text-xs">Host informal gathering where buyer can meet all tenants at once in common area or community room</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm border border-slate-200">
                <i className="ri-door-line text-teal-600 text-xl mt-0.5"></i>
                <div>
                  <p className="font-semibold text-slate-900 text-sm mb-1">Door-to-Door Introductions</p>
                  <p className="text-slate-600 text-xs">Walk through property with buyer, knocking on doors and making personal introductions to available tenants</p>
                </div>
              </div>

              <div className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm border border-slate-200">
                <i className="ri-phone-line text-teal-600 text-xl mt-0.5"></i>
                <div>
                  <p className="font-semibold text-slate-900 text-sm mb-1">Phone or Video Calls</p>
                  <p className="text-slate-600 text-xs">For remote buyers or tenants, facilitate phone or video introductions to establish initial contact</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-8 mb-12">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">Sample Tenant Introduction Letter</h3>
          
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="text-sm text-slate-700 space-y-4">
              <p><strong>Date:</strong> [Current Date]</p>
              <p><strong>To:</strong> All Residents of [Property Name]</p>
              <p><strong>Subject:</strong> Introduction of New Property Owner</p>
              
              <p className="pt-4">Dear Valued Residents,</p>
              
              <p>
                I am pleased to introduce [Buyer Name], the new owner of [Property Name]. The property sale will close on [Closing Date], and [Buyer Name] will assume ownership and management responsibilities at that time.
              </p>
              
              <p>
                <strong>What This Means for You:</strong>
              </p>
              
              <ul className="list-disc pl-6 space-y-2">
                <li>Your lease agreement remains in full effect with all terms unchanged</li>
                <li>Your rent amount and due date will not change</li>
                <li>Your security deposit has been transferred to the new owner</li>
                <li>All maintenance requests will continue to be addressed promptly</li>
              </ul>
              
              <p>
                <strong>New Owner Contact Information:</strong>
              </p>
              
              <div className="bg-slate-50 p-4 rounded-lg">
                <p>Name: [Buyer Name]</p>
                <p>Phone: [Phone Number]</p>
                <p>Email: [Email Address]</p>
                <p>Office Address: [Address]</p>
                <p>Emergency Contact: [Emergency Number]</p>
              </div>
              
              <p>
                <strong>Rent Payment Instructions:</strong>
              </p>
              
              <p>
                Beginning [Date], please send rent payments to: [New Payment Address/Portal]. Detailed payment instructions will be provided separately.
              </p>
              
              <p>
                [Buyer Name] is committed to maintaining the quality and community atmosphere you've come to expect. They look forward to meeting each of you personally in the coming weeks.
              </p>
              
              <p>
                If you have any questions during this transition, please don't hesitate to contact me at [Your Contact] or [Buyer Name] at [Buyer Contact].
              </p>
              
              <p>
                Thank you for being such wonderful residents. I'm confident you'll find [Buyer Name] to be an excellent landlord.
              </p>
              
              <p className="pt-4">
                Sincerely,<br />
                [Your Name]<br />
                [Current Owner]
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">Tips for Successful Buyer-Tenant Meetings</h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-teal-600 mb-4 flex items-center gap-2">
                <i className="ri-checkbox-circle-fill"></i>
                DO: Recommended Actions
              </h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-teal-600 mt-1"></i>
                  <span className="text-slate-700 text-sm">Be present during initial introductions to facilitate conversation</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-teal-600 mt-1"></i>
                  <span className="text-slate-700 text-sm">Speak positively about the buyer and their plans for the property</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-teal-600 mt-1"></i>
                  <span className="text-slate-700 text-sm">Provide buyer with tenant background information beforehand</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-teal-600 mt-1"></i>
                  <span className="text-slate-700 text-sm">Allow buyer to share their vision while reassuring tenants</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-teal-600 mt-1"></i>
                  <span className="text-slate-700 text-sm">Schedule meetings at convenient times for tenants</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-teal-600 mt-1"></i>
                  <span className="text-slate-700 text-sm">Provide written contact information for new owner</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-red-600 mb-4 flex items-center gap-2">
                <i className="ri-close-circle-fill"></i>
                DON'T: Actions to Avoid
              </h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-red-600 mt-1"></i>
                  <span className="text-slate-700 text-sm">Allow buyer to discuss rent increases or major changes before closing</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-red-600 mt-1"></i>
                  <span className="text-slate-700 text-sm">Share negative information about tenants in their presence</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-red-600 mt-1"></i>
                  <span className="text-slate-700 text-sm">Leave buyer alone with tenants before closing is complete</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-red-600 mt-1"></i>
                  <span className="text-slate-700 text-sm">Make promises about buyer's future policies or management style</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-red-600 mt-1"></i>
                  <span className="text-slate-700 text-sm">Rush introductions or make them feel obligatory</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-red-600 mt-1"></i>
                  <span className="text-slate-700 text-sm">Introduce buyer before deal is solid and likely to close</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
